import { EOrderStatus } from "api/enums";
import { compareStrings, isNumber, parseTokenJWTToObject } from "../../utils";
import {
  InvalidCredentialsException,
  InvalidTenantIdException,
  NetworkErrorException,
  NoAccessPermissionException,
  QuotationNotFoundException,
  UserBlockedException,
  UserNotFoundException,
} from "../exceptions/MegaRequestException";
import { NotFoundException } from "../exceptions/NotFoundException";
import {
  IBillingData,
  IBillingDataItem,
  IBranch,
  IBudget,
  ICity,
  IClient,
  ICostCenter,
  IDocumentOrder,
  IDocumentType,
  IFinancialIndicator,
  IIndicatorQuotation,
  IInvoice,
  IPaymentCondition,
  IProduct,
  IProject,
} from "../interfaces/megaERP";

const axios = require("axios");

class MegaERPService {
  private authToken: string = "";
  private readonly defaultHeaders: { [key: string]: string } = {
    "Content-Type": "application/json",
  };

  constructor(
    public readonly host: string,
    public readonly username: string,
    public readonly password: string,
    public readonly tenantId: string
  ) {
    if (!host || !username || !password)
      throw new InvalidCredentialsException();

    if (tenantId) {
      this.defaultHeaders = {
        "Content-Type": "application/json",
        TenantId: tenantId,
      };
    }
  }

  isDifferent(other: MegaERPService) {
    return (
      this.host !== other.host ||
      this.username !== other.username ||
      this.password !== other.password ||
      this.tenantId !== other.tenantId
    );
  }

  /** Autentica o usuário das APIs do Mega e
   * guarda o token de acesso.
   */
  async getAuthToken(): Promise<boolean> {
    return await axios
      .post(
        `${this.host}/api/Auth/SignIn`,
        {
          username: this.username,
          password: this.password,
        },
        { headers: { ...this.defaultHeaders } }
      )
      .then((response) => {
        if (response.status === 200) {
          this.authToken = response.data.accessToken;
          return Boolean(this.authToken);
        }
        return false;
      })
      .catch((error) => {
        const message = this.getResponseMessage(error);
        console.error(error);

        if (message.indexOf("não encontrado") !== -1) {
          throw new UserNotFoundException(this.username);
        }

        if (message.toLowerCase().indexOf("bloqueado") !== -1) {
          throw new UserBlockedException(this.username);
        }

        if (message.indexOf("permissão de acesso") !== -1) {
          throw new NoAccessPermissionException(this.username);
        }

        if (error.response?.status === 500 && message === `""`) {
          throw new InvalidTenantIdException();
        }

        this.logError(error);
      });
  }

  /** Obtém os clientes de uma filial aplicando o filtro de nome
   * fantasia do cliente.
   */
  async getClient(branch: number, name: string): Promise<IClient[]> {
    const headers = this.getAuthenticatedHeader();
    if (!branch) return [];

    return await axios
      .get(`${this.host}/api/globalagente/AgenteCliente/${branch}/${name}`, {
        headers,
      })
      .then((response) => {
        if (response.status !== 200) return [];
        const data = response.data;
        return data
          .map((client) => {
            return {
              id: client.codigoSistema,
              name: client.nomeAgente,
            };
          })
          .sort((a, b) => compareStrings(a.name, b.name));
      })
      .catch((error) => this.logError(error));
  }

  /** Obtém o padrão atual sendo usado na filial que
   * é usado como padrão do cliente. O padrão é outro
   * identificador utilizado no ERP Mega.
   */
  async getClientPattern(branch: number): Promise<number> {
    const headers = this.getAuthenticatedHeader();
    if (!branch) return 0;

    return await axios
      .get(`${this.host}/api/global/PadraoTabela/Atual/${branch}/53`, {
        headers,
      })
      .then((response) => {
        if (response.status !== 200) return;
        const data = response.data;
        return data.padrao;
      })
      .catch((error) => this.logError(error));
  }

  /** Obtém as filiais ativas do usuário autenticado. */
  public async getActivatedBranches(): Promise<IBranch[]> {
    const headers = this.getAuthenticatedHeader();
    return await axios
      .get(`${this.host}/api/globalagente/Organizacao/FiliaisAtivas`, {
        headers,
      })
      .then((response) => {
        if (response.status !== 200) {
          throw new Error("No content");
        }

        const data = response.data;
        return data.map((branch) => {
          return {
            id: branch.id,
            name: branch.agente.nomeFantasia,
            agentId: branch.agente.id,
          };
        });
      });
  }

  /** Obtém as filiais do usuário autenticado. */
  public async getBranches(): Promise<IBranch[]> {
    const headers = this.getAuthenticatedHeader();
    return await axios
      .get(`${this.host}/api/globalagente/Organizacao/Filiais`, {
        headers,
      })
      .then((response) => {
        if (response.status !== 200) return [];
        const data = response.data;
        return data.map((branch) => {
          return {
            id: branch.id,
            name: branch.agente.nomeFantasia,
            agentId: branch.agente.id,
          };
        });
      })
      .catch((error) => this.logError(error));
  }

  /** Obtém a cidade cadastrada para a filial. */
  async getBranchsCity(branchAgentId: string): Promise<string> {
    const headers = this.getAuthenticatedHeader();
    if (!branchAgentId) return "";

    return await axios
      .get(`${this.host}/api/globalagente/EnderecoAgente/${branchAgentId}`, {
        headers,
      })
      .then((response) => {
        if (response.status !== 200) return "";
        const data = response.data;
        return data.cidade;
      })
      .catch((error) => this.logError(error));
  }

  /** Obtém as condições de pagamento do usuário Mega. */
  async getPaymentCondition(): Promise<IPaymentCondition[]> {
    const headers = this.getAuthenticatedHeader();
    return await axios
      .get(`${this.host}/api/global/CondicaoPagamento/ContasAReceber`, {
        headers,
      })
      .then((response) => {
        if (response.status !== 200) return [];
        const data = response.data;
        return data.map((payment) => {
          return {
            id: payment.id,
            code: payment.codigo,
            name: payment.descricao,
          };
        });
      })
      .catch((error) => this.logError(error));
  }

  /** Obtém os tipos de documentos cadastrados para filial. */
  async getDocumentType(
    branch: number,
    userId: string
  ): Promise<IDocumentType[]> {
    const headers = this.getAuthenticatedHeader();
    const params = new URLSearchParams({ expand: "grupo" });
    if (!branch || !userId) return [];

    return await axios
      .get(
        `${this.host}/api/vendascadastros/TipoDocumento/DocumentoVenda/idFilial/${branch}/idUsuario/${userId}`,
        { headers, params }
      )
      .then((response) => {
        if (response.status !== 200) return [];
        const data = response.data;

        return data.map((documentType) => {
          const groups = documentType.grupos
            ? documentType.grupos.map((group) => {
                return group.id;
              })
            : [];

          return {
            id: documentType.id,
            code: documentType.codigo,
            name: documentType.descricao,
            groups,
          };
        });
      })
      .catch((error) => this.logError(error));
  }

  /** Obtém os orçamentos executivos vinculados a filial. */
  async getBudget(branch: number): Promise<IBudget[]> {
    const headers = this.getAuthenticatedHeader();
    if (!branch) return [];

    return await axios
      .get(`${this.host}/api/AdmObra/Orcamento/Filial/${branch}`, { headers })
      .then((response) => {
        if (response.status !== 200) return [];
        const data = response.data;
        return data.map((budget) => {
          return {
            id: budget.id,
            name: budget.descricao,
          };
        });
      })
      .catch((error) => this.logError(error));
  }

  /** Obtém os centros de custos do usuário Mega. */
  async getCostCenter(): Promise<ICostCenter[]> {
    const headers = this.getAuthenticatedHeader();
    return await axios
      .get(`${this.host}/api/global/CentroCusto`, { headers })
      .then((response) => {
        const data = response.data;
        return data.map((cost) => {
          return {
            id: cost.id,
            code: cost.reduzido,
            ide: cost.identificador,
            pattern: cost.padrao,
            name: cost.descricao,
          };
        });
      })
      .catch((error) => {
        console.warn("Error on request global API...", error.message);
        return this.getCostCenterAccountancy();
      });
  }

  /** Obtém os centros de custos do usuário Mega, porém, pelo módulo
   * de contabilidade. Usada quando a função getCostCenter falhar. */
  private async getCostCenterAccountancy(): Promise<ICostCenter[]> {
    const headers = this.getAuthenticatedHeader();
    return await axios
      .get(`${this.host}/api/contabilidadecadastros/CentroCusto`, { headers })
      .then((response) => {
        const data = response.data;
        return data.map((cost) => {
          return {
            id: cost.Id,
            code: cost.Reduzido,
            ide: cost.Identificador,
            pattern: cost.Padrao,
            name: cost.Descricao,
          };
        });
      })
      .catch((error) => this.logError(error));
  }

  /** Obtém os projetos do usuário Mega. */
  async getProject(): Promise<IProject[]> {
    const headers = this.getAuthenticatedHeader();
    return await axios
      .get(`${this.host}/api/global/Projeto`, { headers })
      .then((response) => {
        const data = response.data;
        return data.map((project) => {
          return {
            id: project.id,
            code: project.reduzido,
            ide: project.identificador,
            pattern: project.padrao,
            name: project.descricao,
          };
        });
      })
      .catch((error) => this.logError(error));
  }

  /** Obtém os produtos a partir dos IDs de grupos do centro
   * de custo escolhido.
   */
  async getProductItems(groupsId: string[]): Promise<IProduct[]> {
    const headers = this.getAuthenticatedHeader();
    const params = new URLSearchParams({ expand: "unidade" });
    if (!groupsId) return [];

    return await axios
      .post(`${this.host}/api/produto/Produto/grupo/Lista`, groupsId, {
        headers,
        params,
      })
      .then((response) => {
        const data = response.data;

        return data.map((product) => {
          return {
            codItem: product.codigo,
            description: product.descricao,
            pattern: product.padrao,
            unit: {
              code: product.unidade.codigo,
              pattern: product.unidade.padrao,
            },
          };
        });
      })
      .catch((error) => this.logError(error));
  }

  /** Obtém os municípios do usuário Mega. */
  async getCities(): Promise<ICity[]> {
    const headers = this.getAuthenticatedHeader();
    return await axios
      .get(`${this.host}/api/global/Municipio`, {
        headers,
      })
      .then((response) => {
        if (response.status !== 200) return [];
        const data = response.data;
        return data.map((city) => {
          return {
            id: city.codigoMunicipio,
            name: city.nome,
          };
        });
      })
      .catch((error) => this.logError(error));
  }

  /** Realiza o envio dos dados processados para faturamento
   * no ERP Mega, criando um pedido de geração do documento
   * de venda.
   */
  async sendToBilling({
    branch,
    paymentCondition,
    documentType,
    observation,
    client,
    clientPattern,
    costCenter,
    project,
    items,
    issueDate,
  }: IBillingData): Promise<number> {
    const headers = this.getAuthenticatedHeader();

    const itemsWeb = items.map((item, index) => createItemWeb(item, index));
    const data = JSON.stringify(createObject(itemsWeb));

    return await axios
      .post(`${this.host}/api/pedidovenda/PedidoVendaWeb`, JSON.parse(data), {
        headers,
      })
      .then((response) => {
        const data = response.data;
        if (data.indexOf("sucesso") !== -1) {
          const result = /[0-9]+/.exec(data);
          const orderId = Number(result ? result[0] : 0);

          if (isNumber(orderId)) return orderId;
        }
        return 0;
      })
      .catch((error) => this.logError(error));

    function createObject(itemsWeb) {
      return {
        filial: { id: branch.id },
        condicaoPagamento: paymentCondition.code,
        dataEmissao: issueDate,
        cliente: { padrao: clientPattern, codigo: client.id },
        tipoDocumento: { id: documentType.code },
        projeto: project.code,
        centroCusto: costCenter.code,
        observacoesWeb: [
          {
            tipo: "G",
            descricao: observation,
          },
        ],
        itensWeb: itemsWeb,
      };
    }

    function createItemWeb(item: IBillingDataItem, index: number) {
      const unitValue = item.unitCost;
      return {
        sequencia: index + 1,
        produto: {
          padrao: item.pattern,
          codigo: item.codItem,
          descricao: item.description,
          unidade: { codigo: item.unit.code, padrao: item.unit.pattern },
        },
        centroCustoIDE: costCenter.ide,
        centroCusto: costCenter.code,
        projetoIDE: project.ide,
        projeto: project.code,
        quantidade: 1,
        valorUnitario: unitValue,
        valorMaoObra: item.materialUsed ? 0 : unitValue,
        valorMercadoriaEmpregada: item.materialUsed ? unitValue : 0,
        // desconto: { valor: item.discount },
        caucao: { valor: item.retention },
      };
    }
  }

  /** Obtém o status do pedido para geração de venda enviado. */
  async getBillingOrder(orderId: number): Promise<IDocumentOrder> {
    const headers = this.getAuthenticatedHeader();
    const resultError = {
      code: 0,
      organization: 0,
      serial: "",
      status: "error" as any,
    };
    if (!orderId) return resultError;

    const apiStatus = {
      N: EOrderStatus.PROCESSING,
      M: EOrderStatus.PROCESSING,
      I: EOrderStatus.IMPORTED,
      C: EOrderStatus.CANCELED,
    };

    return await axios
      .get(`${this.host}/api/pedidovenda/PedidoVendaWeb/${orderId}`, {
        headers,
      })
      .then((response) => {
        const data = response.data;
        if (!data || data.log) return { ...resultError, error: data.log };

        const status = apiStatus[data.status] || EOrderStatus.PROCESSING;
        return {
          code: data.codigo || 0,
          organization: data.organizacao || 0,
          serial: data.serie || "",
          status,
        };
      })
      .catch((error) => this.logError(error));
  }

  /** Obtém as notas fiscais vinculadas ao documento de venda
   * gerado pelo pedido enviado.
   */
  async getOrderInvoices(
    branchId: number,
    orderCode: number,
    serial: string
  ): Promise<IInvoice[]> {
    const headers = this.getAuthenticatedHeader();
    if (!branchId || !orderCode || !serial) return [];

    return await axios
      .get(
        `${this.host}/api/documentosvinculados/DocumentosVinculados/${branchId}/${orderCode}/${serial}`,
        { headers }
      )
      .then((response) => {
        const data = response.data?.notaFiscal;
        if (!data) return [];

        return data.map((invoice) => ({
          number: invoice.numero,
          serial: invoice.serie,
          issuanceDate: new Date(invoice.dataEmissao),
        }));
      })
      .catch((error) => {
        const message = this.getResponseMessage(error);

        if (message.indexOf("Pedido não encontrado") !== -1) {
          console.error(error);
          throw new NotFoundException("order");
        }

        this.logError(error);
      });
  }

  public async getOrderIssueDate(
    organization: number,
    serial: string,
    code: number
  ): Promise<Date | undefined> {
    const headers = this.getAuthenticatedHeader();
    if (!organization || !serial || !code) return undefined;

    return await axios
      .get(
        `${this.host}/api/pedidovenda/PedidoVenda/${organization}/${serial}/${code}`,
        { headers }
      )
      .then((response) => {
        const date = response.data?.dataEmissao;
        return date ? new Date(date) : undefined;
      })
      .catch((error) => {
        const message = this.getResponseMessage(error);

        if (message.indexOf("Pedido não encontrado") !== -1) {
          console.error(error);
          throw new NotFoundException("order");
        }

        this.logError(error);
      });
  }

  /** Obtém o valor total do orçamento executivo. */
  async getBudgetTotal(budgetId: string): Promise<number> {
    const headers = this.getAuthenticatedHeader();
    if (!budgetId) return 0;

    return await axios
      .get(`${this.host}/api/AdmObra/Orcamento/ValorTotal/${budgetId}`, {
        headers,
      })
      .then((response) => {
        const data = response.data;
        return data?.ValorTotal || 0;
      })
      .catch((error) => this.logError(error));
  }

  /** Obtém o valor agregado do orçamento executivo. */
  async getBudgetAddedTotal(budgetId: string): Promise<number> {
    const headers = this.getAuthenticatedHeader();
    if (!budgetId) return 0;

    const req = axios
      .get(
        `${this.host}/api/AdmObra/Medicao/ValorAgregadoComTaxas/Orcamento/${budgetId}`,
        { headers }
      )
      .catch(() => {
        console.error("Valor Agregado com taxas não foi encontrado!");
        return axios.get(
          `${this.host}/api/AdmObra/Medicao/ValorAgregado/Orcamento/${budgetId}`,
          { headers }
        );
      });

    return await req
      .then((response) => {
        const data = response.data;
        return data?.ValorAgregado || 0;
      })
      .catch((error) => this.logError(error));
  }

  /** Obtém o valor realizado do orçamento executivo. */
  async getBudgetRealizedTotal(budgetId: string): Promise<number> {
    const headers = this.getAuthenticatedHeader();
    if (!budgetId) return 0;

    return await axios
      .get(
        `${this.host}/api/AdmObra/Realizado/ValorRealizado/Orcamento/${budgetId}`,
        { headers }
      )
      .then((response) => {
        const data = response.data;
        return data?.ValorRealizado || 0;
      })
      .catch((error) => this.logError(error));
  }

  /** Obtém os indicadores financeiros do usuário Mega. */
  async getFinancialIndicators(): Promise<IFinancialIndicator[]> {
    const headers = this.getAuthenticatedHeader();
    return await axios
      .get(`${this.host}/api/global/IndiceFinanceiro`, {
        headers,
      })
      .then((response) => {
        if (response.status !== 200) return [];
        const data = response.data;
        return data.map((indicator) => {
          return {
            id: indicator.id,
            name: indicator.nome,
          };
        });
      })
      .catch((error) => this.logError(error));
  }

  /** Obtém a cotação na data informada do indicador
   * financeiro.
   */
  async getFinancialIndicatorQuotation(
    indicator: IFinancialIndicator,
    date: Date
  ): Promise<IIndicatorQuotation | undefined> {
    if (!date || !indicator || (!indicator.id && indicator.id !== 0)) return;

    const headers = this.getAuthenticatedHeader();
    const convertedDate = date.toJSON().split("T")[0];

    return await axios
      .get(
        `${this.host}/api/global/IndiceFinanceiro/${indicator.id}/Cotacao/${convertedDate}`,
        {
          headers,
        }
      )
      .then((response) => {
        if (response.status !== 200) return undefined;
        const quota = response.data;
        return {
          date: new Date(quota.data),
          value: quota.valor || 0,
        };
      })
      .catch((error) => {
        if (error.response?.status === 404) {
          console.log(error);
          throw new QuotationNotFoundException(indicator.name, date);
        }

        this.logError(error);
      });
  }

  /** Obtém o ID do usuário Mega a partir do token de acesso. */
  getUserIdFromToken(): string {
    const jsonData = parseTokenJWTToObject(this.authToken);
    const user = Object.entries(jsonData).find(([key]) =>
      key.includes("claims/nameidentifier")
    );
    if (user) return String(user[1]);
    return "";
  }

  private getAuthenticatedHeader() {
    return {
      ...this.defaultHeaders,
      Authorization: `Bearer ${this.authToken}`,
    };
  }

  logError(error) {
    console.error(error);

    if (error.message.indexOf("code 401") !== -1)
      throw new InvalidCredentialsException();

    if (error.message.indexOf("Network Error") !== -1)
      throw new NetworkErrorException();

    throw error;
  }

  private getResponseMessage(error): string {
    return JSON.stringify(error.response?.data) || "";
  }
}

export default MegaERPService;
